import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { ShieldCheck, RefreshCcw, History, Loader2, ChevronDown, ChevronRight } from "lucide-react";

function relTime(iso) {
    if (!iso) return null;
    try {
        const t = new Date(iso).getTime();
        const diff = Date.now() - t;
        const min = Math.floor(diff / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min} min ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const d = Math.floor(hr / 24);
        if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso;
    }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export function SaferReverifyCard({ stats, onRefreshStats }) {
    const [running, setRunning] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const summary = stats?.last_safer_reverify_summary;
    const lastAt = stats?.last_safer_reverify_at;

    const onRunNow = async () => {
        setRunning(true);
        try {
            await api.post("/admin/safer-reverify/runs");
            toast.success("Re-verify started — refreshing summary…");
            // Poll for ~12s; the run finishes quickly for small customer counts
            for (let i = 0; i < 6; i++) {
                await new Promise((r) => setTimeout(r, 2000));
                await onRefreshStats?.();
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setRunning(false);
        }
    };

    return (
        <>
            <Card data-testid="admin-card-safer-reverify" className="h-full">
                <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
                    <div className="space-y-1.5">
                        <CardTitle className="text-base font-semibold text-slate-900">
                            SAFER re-verify
                        </CardTitle>
                        <CardDescription className="text-sm text-slate-500">
                            Daily background sync · runs at 06:00 UTC
                        </CardDescription>
                    </div>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-900">
                        <ShieldCheck size={18} />
                    </span>
                </CardHeader>
                <CardContent>
                    <div className="text-xs text-slate-500" data-testid="safer-reverify-last">
                        {lastAt ? (
                            <>
                                Last run <span className="text-slate-900 font-medium">{relTime(lastAt)}</span>
                                <span className="text-slate-400"> · {fmtDateTime(lastAt)}</span>
                            </>
                        ) : (
                            "No runs yet"
                        )}
                    </div>
                    {summary && (
                        <p className="mt-1 text-sm text-slate-700" data-testid="safer-reverify-summary">
                            Checked <span className="font-semibold">{summary.checked}</span>
                            {" · "}<span className={summary.raised > 0 ? "text-red-700 font-semibold" : "text-slate-700"}>{summary.raised} raised</span>
                            {" · "}<span className={summary.cleared > 0 ? "text-emerald-700 font-semibold" : "text-slate-700"}>{summary.cleared} cleared</span>
                            {" · "}<span className={summary.errors > 0 ? "text-amber-700 font-semibold" : "text-slate-700"}>{summary.errors} errors</span>
                        </p>
                    )}
                    <div className="mt-4 flex gap-2">
                        <Button
                            size="sm"
                            onClick={onRunNow}
                            disabled={running}
                            data-testid="safer-reverify-run-now"
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        >
                            {running ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />}
                            {running ? "Running…" : "Run now"}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setHistoryOpen(true)}
                            data-testid="safer-reverify-history-open"
                            className="gap-1.5"
                        >
                            <History size={14} /> View history
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <SaferReverifyHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
        </>
    );
}

function SaferReverifyHistoryDialog({ open, onOpenChange }) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [details, setDetails] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/safer-reverify/runs", { params: { limit: 30 } });
            setRuns(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    const toggleDetail = async (id) => {
        if (expandedId === id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(id);
        if (!details[id]) {
            try {
                const { data } = await api.get(`/admin/safer-reverify/runs/${id}`);
                setDetails((d) => ({ ...d, [id]: data }));
            } catch (err) {
                toast.error(formatApiError(err));
            }
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="safer-reverify-history-dialog">
                <DialogHeader>
                    <DialogTitle>SAFER re-verify history</DialogTitle>
                    <DialogDescription>Last 30 runs — click a row to inspect the per-customer delta list.</DialogDescription>
                </DialogHeader>
                {loading && <p className="text-sm text-slate-500">Loading…</p>}
                {!loading && runs.length === 0 && (
                    <p className="text-sm text-slate-500">No runs recorded yet.</p>
                )}
                <div className="max-h-[60vh] overflow-y-auto">
                    {runs.map((r) => {
                        const isOpen = expandedId === r.id;
                        const detail = details[r.id];
                        return (
                            <div key={r.id} className="border-b border-slate-100 last:border-b-0">
                                <button
                                    type="button"
                                    onClick={() => toggleDetail(r.id)}
                                    className="w-full flex items-center justify-between gap-3 py-3 px-1 text-left hover:bg-slate-50 rounded-md"
                                    data-testid={`safer-reverify-row-${r.id}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        {isOpen ? <ChevronDown size={14} className="text-slate-400 shrink-0" /> : <ChevronRight size={14} className="text-slate-400 shrink-0" />}
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-slate-900 truncate">
                                                {fmtDateTime(r.started_at)}
                                                {r.trigger === "manual" && (
                                                    <span className="ml-2 inline-block rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">manual</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                Checked {r.total_checked} · {r.duration_seconds}s
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-xs text-slate-700 flex items-center gap-3 shrink-0">
                                        <span className={r.flag_raised > 0 ? "text-red-700 font-semibold" : ""}>{r.flag_raised} raised</span>
                                        <span className={r.flag_cleared > 0 ? "text-emerald-700 font-semibold" : ""}>{r.flag_cleared} cleared</span>
                                        <span className={r.errors > 0 ? "text-amber-700 font-semibold" : ""}>{r.errors} errors</span>
                                    </div>
                                </button>
                                {isOpen && detail && (
                                    <div className="px-7 pb-3 text-xs text-slate-700 space-y-1">
                                        {(detail.delta_list || []).length === 0 && <p className="text-slate-500">No customers were checked in this run.</p>}
                                        {(detail.delta_list || []).map((d) => (
                                            <div key={d.customer_id} className="flex items-center justify-between gap-3 py-1 border-b border-slate-50 last:border-0">
                                                <div className="min-w-0">
                                                    <span className="font-medium text-slate-900">{d.company_name || "(no name)"}</span>
                                                    <span className="text-slate-500 ml-2 font-mono text-[11px]">USDOT {d.usdot}</span>
                                                </div>
                                                <span className={`text-[11px] uppercase tracking-wider rounded-full px-2 py-0.5 ${
                                                    d.change === "raised" ? "bg-red-50 text-red-700" :
                                                    d.change === "cleared" ? "bg-emerald-50 text-emerald-700" :
                                                    d.change === "error" ? "bg-amber-50 text-amber-800" :
                                                    "bg-slate-100 text-slate-600"
                                                }`}>{d.change}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </DialogContent>
        </Dialog>
    );
}
